import { useEffect, useReducer, useState } from "react";
import { PomodoroWidgetSettingsEntity } from "@lifeat/shared/data-models/widget/Pomodoro/PomodoroWidgetSettingsEntity";
import { DEFAULT_ALARM_OBJ } from "@lifeat/shared/widget/PomodoroTimerConstants";
import { PomodoroTimerType } from "@lifeat/shared/widget/PomodoroTimerTypes";
import { PomodoroService } from "../../../../service/PomodoroService";
import {
    LONG_BREAK_TIME_MIN,
    POMODORO_TIME_MIN,
    SHORT_BREAK_TIME_MIN,
    TIMER_DURATION_MINS_MAX_VALUE,
    TIMER_DURATION_MINS_MIN_VALUE,
} from "../TimerWidgetConstants";

export type PomodoroSettingsInput = {
    selectedAlarmSound: string;
    isAutoStartTimer: boolean;
    dataChanged: boolean;
    pomodoroTimerDuration: string;
    shortBreakTimerDuration: string;
    longBreakTimerDuration: string;
    timerType: PomodoroTimerType;
    hidePomodoroCount: boolean;
    // Timer volume is not here because it is managed by `TimerVolumeContext`.
};

export type PomodoroSettingsInputAction =
    | { type: "load"; value: PomodoroWidgetSettingsEntity }
    | { type: "setSelectedAlarmSound"; value: string }
    | { type: "toggleIsAutoStartTimer" }
    | { type: "setDataChanged"; value: boolean }
    | { type: "setPomodoroTimerDuration"; value: string }
    | { type: "setShortBreakTimerDuration"; value: string }
    | { type: "setLongBreakTimerDuration"; value: string }
    | { type: "setTimerType"; value: PomodoroTimerType }
    | { type: "toggleHidePomodoroCount" };

function isValidTimerDuration(input: string): boolean {
    const numVal = parseInt(input);
    return (
        input === "" ||
        (!isNaN(numVal) && numVal >= TIMER_DURATION_MINS_MIN_VALUE && numVal <= TIMER_DURATION_MINS_MAX_VALUE)
    );
}

function pomodoroSettingsInputReducer(
    state: PomodoroSettingsInput,
    action: PomodoroSettingsInputAction,
): PomodoroSettingsInput {
    switch (action.type) {
        case "load":
            return {
                ...state,
                ...settingsToInput(action.value),
            };
        case "setSelectedAlarmSound":
            return { ...state, selectedAlarmSound: action.value };
        case "toggleIsAutoStartTimer":
            return {
                ...state,
                isAutoStartTimer: !state.isAutoStartTimer,
                dataChanged: true,
            };
        case "setDataChanged":
            return { ...state, dataChanged: action.value };
        case "setPomodoroTimerDuration":
            if (isValidTimerDuration(action.value)) {
                return {
                    ...state,
                    pomodoroTimerDuration: action.value,
                    dataChanged: true,
                };
            }
            return state;
        case "setShortBreakTimerDuration":
            if (isValidTimerDuration(action.value)) {
                return {
                    ...state,
                    shortBreakTimerDuration: action.value,
                    dataChanged: true,
                };
            }
            return state;
        case "setLongBreakTimerDuration":
            if (isValidTimerDuration(action.value)) {
                return {
                    ...state,
                    longBreakTimerDuration: action.value,
                    dataChanged: true,
                };
            }
            return state;
        case "setTimerType":
            return { ...state, timerType: action.value };
        case "toggleHidePomodoroCount":
            return {
                ...state,
                hidePomodoroCount: !state.hidePomodoroCount,
                dataChanged: true,
            };
    }
}

const DEFAULT_STATE: PomodoroSettingsInput = {
    selectedAlarmSound: Object.keys(DEFAULT_ALARM_OBJ)[0],
    isAutoStartTimer: true,
    dataChanged: false,
    pomodoroTimerDuration: String(POMODORO_TIME_MIN),
    shortBreakTimerDuration: String(SHORT_BREAK_TIME_MIN),
    longBreakTimerDuration: String(LONG_BREAK_TIME_MIN),
    timerType: "pomodoro",
    hidePomodoroCount: false,
};

const durationFromInput = (type: PomodoroTimerType, input: PomodoroSettingsInput) => {
    const value = parseInt(
        {
            pomodoro: input.pomodoroTimerDuration,
            short_break: input.shortBreakTimerDuration,
            long_break: input.longBreakTimerDuration,
        }[type],
    );
    return isNaN(value) ? 1 : value;
};

const settingsToInput = (pomodoroSettings: PomodoroWidgetSettingsEntity): Partial<PomodoroSettingsInput> => {
    const input: Partial<PomodoroSettingsInput> = {};
    if (pomodoroSettings.autoStartTimer != null) {
        input.isAutoStartTimer = pomodoroSettings.autoStartTimer;
    }
    if (pomodoroSettings.pomodoroDuration != null) {
        input.pomodoroTimerDuration = String(pomodoroSettings.pomodoroDuration);
    }
    if (pomodoroSettings.shortBreakDuration != null) {
        input.shortBreakTimerDuration = String(pomodoroSettings.shortBreakDuration);
    }
    if (pomodoroSettings.longBreakDuration != null) {
        input.longBreakTimerDuration = String(pomodoroSettings.longBreakDuration);
    }
    if (pomodoroSettings.hidePomodoroCount != null) {
        input.hidePomodoroCount = pomodoroSettings.hidePomodoroCount;
    }
    return input;
};

const inputToSettings = (input: PomodoroSettingsInput): Partial<PomodoroWidgetSettingsEntity> => ({
    autoStartTimer: input.isAutoStartTimer,
    pomodoroDuration: durationFromInput("pomodoro", input),
    shortBreakDuration: durationFromInput("short_break", input),
    longBreakDuration: durationFromInput("long_break", input),
    hidePomodoroCount: input.hidePomodoroCount,
});

export function usePomodoroSettingsInput(pomodoroService: PomodoroService, roomId?: string) {
    const [pomodoroSettingsInput, dispatchPomodoroSettingsInput] = useReducer(
        pomodoroSettingsInputReducer,
        DEFAULT_STATE,
    );
    const [settingsFromStore, setSettingsFromStore] = useState<PomodoroWidgetSettingsEntity | null>(null);

    useEffect(() => {
        let mounted = true;
        if (roomId && mounted) {
            pomodoroService.fetchSettings().then((settings) => {
                // If the component unmounts, we don't want to attempt to set state
                // while this promise is resolving.
                if (mounted) {
                    setSettingsFromStore(settings);
                }
            });
        }
        return () => {
            mounted = false;
        };
    }, [roomId]);

    useEffect(() => {
        if (settingsFromStore) {
            dispatchPomodoroSettingsInput({ type: "load", value: settingsFromStore });
        }
    }, [settingsFromStore]);

    const duration = (type: PomodoroTimerType) => durationFromInput(type, pomodoroSettingsInput);

    const saveSettings = async () => {
        if (pomodoroSettingsInput.dataChanged) {
            const updatedSettings = inputToSettings(pomodoroSettingsInput);
            pomodoroService.updateSettings?.(updatedSettings).then(() => {
                setSettingsFromStore(updatedSettings);
            });

            dispatchPomodoroSettingsInput({ type: "setDataChanged", value: false });
        }
    };

    return {
        pomodoroSettingsInput: pomodoroSettingsInput,
        dispatchPomodoroSettingsInput,
        duration,
        saveSettings,
    };
}
